import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ListTree,
  BookOpen,
  BookText,
  Scale,
  TrendingUp,
  Building2,
  Users,
  LogOut,
  ChevronDown,
  Check,
  Columns3,
  Receipt,
  ShoppingCart,
  UserRound,
  Truck,
  FileSignature,
  Wallet,
  Menu,
  Landmark,
  FileSpreadsheet,
  GitCompareArrows,
  Send,
  PiggyBank,
  Banknote,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import DemoBanner from "./DemoBanner";
import SubscriptionBanner, { SubscriptionNotice } from "./SubscriptionBanner";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";

const NAV = [
  { to: "/app/dashboard", label: "Resumen", icon: LayoutDashboard, tid: "nav-dashboard", module: "dashboard" },
  { to: "/app/plan-cuentas", label: "Plan de cuentas", icon: ListTree, tid: "nav-plan-cuentas", module: "plan_cuentas" },
  { to: "/app/libro-diario", label: "Libro diario", icon: BookOpen, tid: "nav-libro-diario", module: "libro_diario" },
  { to: "/app/libro-mayor", label: "Libro mayor", icon: BookText, tid: "nav-libro-mayor", module: "libro_mayor" },
];

const REPORTES_NAV = [
  { to: "/app/balance-general", label: "Balance general", icon: Scale, tid: "nav-balance-general", module: "reportes" },
  { to: "/app/balance-8-columnas", label: "Balance 8 columnas", icon: Columns3, tid: "nav-balance-8", module: "reportes" },
  { to: "/app/estado-resultados", label: "Estado de resultados", icon: TrendingUp, tid: "nav-estado-resultados", module: "reportes" },
  { to: "/app/flujo-caja", label: "Flujo de caja", icon: Wallet, tid: "nav-flujo-caja", module: "reportes" },
];

const COMERCIAL_NAV = [
  { to: "/app/clientes", label: "Clientes", icon: UserRound, tid: "nav-clientes", module: "comercial" },
  { to: "/app/proveedores", label: "Proveedores", icon: Truck, tid: "nav-proveedores", module: "comercial" },
  { to: "/app/libro-ventas", label: "Libro de ventas", icon: Receipt, tid: "nav-libro-ventas", module: "comercial" },
  { to: "/app/libro-compras", label: "Libro de compras", icon: ShoppingCart, tid: "nav-libro-compras", module: "comercial" },
  { to: "/app/boletas-honorarios", label: "Boletas honorarios", icon: FileSignature, tid: "nav-boletas-honorarios", module: "comercial" },
];

const REMUNERACIONES_NAV = [
  { to: "/app/remuneraciones", label: "Remuneraciones", icon: Banknote, tid: "nav-remuneraciones", module: "remuneraciones" },
];

const TESORERIA_NAV = [
  { to: "/app/cuentas-bancarias", label: "Cuentas bancarias", icon: Landmark, tid: "nav-cuentas-bancarias", module: "tesoreria" },
  { to: "/app/cartola", label: "Cartola bancaria", icon: FileSpreadsheet, tid: "nav-cartola", module: "tesoreria" },
  { to: "/app/conciliacion", label: "Conciliación", icon: GitCompareArrows, tid: "nav-conciliacion", module: "tesoreria" },
  { to: "/app/pagos-proveedores", label: "Pagos a proveedores", icon: Send, tid: "nav-pagos-proveedores", module: "tesoreria" },
  { to: "/app/caja-chica", label: "Caja chica", icon: PiggyBank, tid: "nav-caja-chica", module: "tesoreria" },
];

const ADMIN_NAV = [
  { to: "/app/empresas", label: "Empresas", icon: Building2, tid: "nav-empresas", module: "empresas" },
  { to: "/app/usuarios", label: "Usuarios", icon: Users, tid: "nav-usuarios", module: "usuarios" },
];

function filterNav(items, canRead) {
  return items.filter((i) => canRead(i.module));
}

function canManageOrganization(user) {
  if (!user || user.solo_lectura) return false;
  if (user.role === "superadmin") return true;
  return ["owner", "admin"].includes(user.role);
}

function SidebarContent({ user, onNav }) {
  const { canRead } = usePermissions();
  const nav = filterNav(NAV, canRead);
  const reportes = filterNav(REPORTES_NAV, canRead);
  const comercial = filterNav(COMERCIAL_NAV, canRead);
  const remuneraciones = filterNav(REMUNERACIONES_NAV, canRead);
  const tesoreria = filterNav(TESORERIA_NAV, canRead);
  const adminNav = canManageOrganization(user) ? filterNav(ADMIN_NAV, canRead) : [];

  return (
    <>
      <div className="px-5 py-5 border-b border-[#E2DFD8]">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm bg-[#1B4332] flex items-center justify-center text-white font-display font-semibold text-sm">
            A
          </div>
          <div>
            <div className="font-display font-semibold text-[15px] leading-tight tracking-tight">
              Aequor
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#A3ABA6] truncate max-w-[140px]">
              {user?.organizacion?.nombre || "ERP Contable"}
            </div>
            {user?.organizacion_id && !user?.solo_lectura && (
              <SubscriptionNotice org={user.organizacion} compact />
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {nav.map((item) => (
          <NavItem key={item.to} {...item} onNav={onNav} />
        ))}
        {reportes.length > 0 && (
          <>
            <div className="px-3 pt-4 pb-1.5 text-[10px] tracking-[0.2em] uppercase text-[#A3ABA6] font-semibold">
              Reportes
            </div>
            {reportes.map((item) => (
              <NavItem key={item.to} {...item} onNav={onNav} />
            ))}
          </>
        )}
        {comercial.length > 0 && (
          <>
            <div className="px-3 pt-4 pb-1.5 text-[10px] tracking-[0.2em] uppercase text-[#A3ABA6] font-semibold">
              Comercial
            </div>
            {comercial.map((item) => (
              <NavItem key={item.to} {...item} onNav={onNav} />
            ))}
          </>
        )}
        {remuneraciones.length > 0 && (
          <>
            <div className="px-3 pt-4 pb-1.5 text-[10px] tracking-[0.2em] uppercase text-[#A3ABA6] font-semibold">
              Remuneraciones
            </div>
            {remuneraciones.map((item) => (
              <NavItem key={item.to} {...item} onNav={onNav} />
            ))}
          </>
        )}
        {tesoreria.length > 0 && (
          <>
            <div className="px-3 pt-4 pb-1.5 text-[10px] tracking-[0.2em] uppercase text-[#A3ABA6] font-semibold">
              Tesorería
            </div>
            {tesoreria.map((item) => (
              <NavItem key={item.to} {...item} onNav={onNav} />
            ))}
          </>
        )}
        {adminNav.length > 0 && (
          <>
            <div className="px-3 pt-4 pb-1.5 text-[10px] tracking-[0.2em] uppercase text-[#A3ABA6] font-semibold">
              Administración
            </div>
            {adminNav.map((item) => (
              <NavItem key={item.to} {...item} onNav={onNav} />
            ))}
          </>
        )}
      </nav>
    </>
  );
}

export default function AppLayout() {
  const { user, logout, setEmpresaActiva, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [empresas, setEmpresas] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);

  const loadEmpresas = async () => {
    try {
      const { data } = await api.get("/empresas");
      setEmpresas(data);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  useEffect(() => {
    loadEmpresas();
  }, []);

  // Close mobile sheet on route change
  useEffect(() => { setSheetOpen(false); }, [location.pathname]);

  const empresaActiva =
    empresas.find((e) => e.id === user?.empresa_activa_id) || empresas[0];

  const handleSwitchEmpresa = async (eid) => {
    try {
      await setEmpresaActiva(eid);
      toast.success("Empresa cambiada");
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-[#F9F8F6]" data-testid="app-layout">
      {/* Desktop Sidebar (md+) */}
      <aside className="hidden md:flex w-60 shrink-0 border-r border-[#E2DFD8] bg-white flex-col">
        <SidebarContent user={user} />

        <div className="border-t border-[#E2DFD8] p-3">
          <div className="px-2 py-1.5">
            <div className="text-[13px] font-medium text-[#1C211F] truncate">{user?.name}</div>
            <div className="text-[11px] text-[#59645E]">
              {user?.role_label || user?.role}
            </div>
            {user?.organizacion_id && !user?.solo_lectura && (
              <SubscriptionNotice org={user.organizacion} compact />
            )}
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full mt-1 flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] text-[#59645E] hover:bg-[#F9F8F6] hover:text-[#A94438] transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Drawer */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-white flex flex-col" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
          <SheetDescription className="sr-only">Navega entre los módulos de Aequor.</SheetDescription>
          <SidebarContent user={user} onNav={() => setSheetOpen(false)} />
          <div className="border-t border-[#E2DFD8] p-3">
            <div className="px-2 py-1.5">
              <div className="text-[13px] font-medium text-[#1C211F] truncate">{user?.name}</div>
              <div className="text-[11px] text-[#59645E]">
                {user?.role_label || user?.role}
              </div>
              {user?.organizacion_id && !user?.solo_lectura && (
                <SubscriptionNotice org={user.organizacion} compact />
              )}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button-mobile"
              className="w-full mt-1 flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] text-[#59645E] hover:bg-[#F9F8F6] hover:text-[#A94438] transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} /> Cerrar sesión
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-[#E2DFD8] bg-white px-3 sm:px-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Hamburger (mobile only) */}
            <button
              onClick={() => setSheetOpen(true)}
              data-testid="mobile-menu-button"
              className="md:hidden p-2 -ml-1 rounded-sm hover:bg-[#F9F8F6] text-[#1C211F]"
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="empresa-selector"
                  className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-sm hover:bg-[#F9F8F6] text-left min-w-0"
                >
                  <Building2 className="h-4 w-4 text-[#59645E] shrink-0" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#A3ABA6] leading-none hidden sm:block">
                      Empresa activa
                    </div>
                    <div className="text-[13px] font-medium text-[#1C211F] leading-tight truncate max-w-[160px] sm:max-w-none">
                      {empresaActiva?.nombre || "—"}
                    </div>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-[#59645E] ml-1 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel>Cambiar empresa</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {empresas.map((e) => (
                  <DropdownMenuItem
                    key={e.id}
                    data-testid={`empresa-option-${e.id}`}
                    onClick={() => handleSwitchEmpresa(e.id)}
                    className="flex justify-between items-center"
                  >
                    <div>
                      <div className="text-[13px] font-medium">{e.nombre}</div>
                      <div className="text-[11px] text-[#59645E] font-mono">{e.rut}</div>
                    </div>
                    {empresaActiva?.id === e.id && (
                      <Check className="h-3.5 w-3.5 text-[#1B4332]" />
                    )}
                  </DropdownMenuItem>
                ))}
                {empresas.length === 0 && (
                  <div className="px-2 py-3 text-[12px] text-[#59645E]">
                    No tienes empresas. Crea una en Administración → Empresas.
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden lg:inline text-[11px] tracking-[0.2em] uppercase text-[#A3ABA6]">
              {new Date().toLocaleDateString("es-CL", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span className="lg:hidden text-[11px] tracking-[0.15em] uppercase text-[#A3ABA6]">
              {new Date().toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}
            </span>
          </div>
        </header>

        <DemoBanner />
        <SubscriptionBanner />
        <main className="flex-1 overflow-auto" data-testid="app-main">
          <Outlet context={{ refreshEmpresas: loadEmpresas, refreshUser: refresh, empresaActiva }} />
        </main>
      </div>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, tid, onNav }) {
  return (
    <NavLink
      to={to}
      data-testid={tid}
      onClick={onNav}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] transition-colors ${
          isActive
            ? "bg-[#1B4332] text-white"
            : "text-[#59645E] hover:bg-[#F9F8F6] hover:text-[#1C211F]"
        }`
      }
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} />
      {label}
    </NavLink>
  );
}
